import { useSnackbar } from 'notistack'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  buildCalendarEventData,
  findAppointmentType,
} from 'src/modules/booking/components/booking-confirmed-step/booking-confirmed-step.domain'
import type { AppointmentTypeForConfirmed } from 'src/modules/booking/components/booking-confirmed-step/booking-confirmed-step.interface'
import { useBookingById, useProviderDetails } from 'src/modules/booking/hooks'
import { useNoReferrerPolicy } from 'src/modules/booking/hooks/use-no-referrer-policy'
import { routeNames } from 'src/modules/core/constants/routeNames'
import { LoadingWrapperContext } from 'src/modules/core/context'
import { useRescheduleAllowed, useRescheduleContextByUrl } from 'src/modules/reschedule/hooks'

import {
  getDisplayDataFromRescheduleContext,
  getTimeRangeLabelForDisplay,
  isAppointmentEndInTheFuture,
  shouldLoadBookingForCalendar,
  shouldShowAppointmentDetails,
} from './booking-details.domain'

export function useBookingDetails() {
  const { disableLoading } = useContext(LoadingWrapperContext)
  useNoReferrerPolicy()
  const { bookingId } = useParams<{ bookingId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { enqueueSnackbar } = useSnackbar()

  const identifier = bookingId ?? ''

  const {
    data: context,
    isLoading: isLoadingContext,
    isError: isContextError,
  } = useRescheduleContextByUrl(identifier, { enabled: !!identifier })

  const needBookingForCalendar = shouldLoadBookingForCalendar(identifier, context)
  const { data: booking } = useBookingById(identifier, {
    enabled: needBookingForCalendar,
  })

  const displayData = useMemo(
    () =>
      context
        ? getDisplayDataFromRescheduleContext(context)
        : {
            selectedDate: null,
            selectedTime: null,
            endTime: null,
            timezone: null,
            appointmentType: null,
          },
    [context],
  )

  const appointmentType = useMemo(
    () =>
      context
        ? findAppointmentType(
            (context.appointment_types ?? []) as AppointmentTypeForConfirmed[],
            context.selected_appointment_type_id ?? context.initialData?.appointmentType,
          )
        : null,
    [context],
  )

  const providerName = context?.providerName ?? ''
  const providerId = context?.providerId
  const { data: providerDetails } = useProviderDetails(providerId ?? '', { enabled: !!providerId })

  const rescheduleUrlCode = useMemo(() => {
    if (context?.source === 'event' && context.url_code) return context.url_code
    return identifier
  }, [context, identifier])
  // An evn_* identifier is not a code the check accepts, so wait for the context's url_code.
  const canReschedule = useRescheduleAllowed(context ? rescheduleUrlCode : '')

  const calendarEventData = useMemo(
    () =>
      buildCalendarEventData({
        bookingId: identifier,
        booking: context?.source === 'booking' ? (booking ?? null) : null,
        bookingData: null,
        displayData,
        appointmentType,
        providerName,
        canReschedule,
      }),
    [identifier, context?.source, booking, displayData, appointmentType, providerName, canReschedule],
  )

  const timeRangeLabel = useMemo(
    () => getTimeRangeLabelForDisplay(displayData, appointmentType),
    [displayData, appointmentType],
  )

  const showAppointmentDetails = useMemo(
    () => shouldShowAppointmentDetails(displayData, appointmentType, providerName),
    [displayData, appointmentType, providerName],
  )

  const isFutureAppointment = isAppointmentEndInTheFuture(context?.end_time_utc)

  useEffect(() => {
    disableLoading()
  }, [disableLoading])

  useEffect(() => {
    if (searchParams.get('rescheduled') === '1') {
      enqueueSnackbar('Your appointment has been rescheduled.', {
        variant: 'success',
        autoHideDuration: 5000,
      })
      const next = new URLSearchParams(searchParams)
      next.delete('rescheduled')
      setSearchParams(next, { replace: true })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- run once on mount when coming back from reschedule

  const handleRescheduleClick = useCallback(() => {
    if (rescheduleUrlCode) navigate(routeNames.reschedule.path.replace(':urlCode', rescheduleUrlCode))
  }, [rescheduleUrlCode, navigate])

  const isLoading = isLoadingContext
  const hasError = isContextError || (!context && !!identifier)
  const showRescheduleButton = showAppointmentDetails && canReschedule && !!rescheduleUrlCode && isFutureAppointment

  return {
    isLoading,
    hasError,
    showAppointmentDetails,
    showRescheduleButton,
    providerId,
    data: {
      displayData,
      appointmentType,
      providerName,
      logoUrl: providerDetails?.logo_url,
      timeRangeLabel,
      calendarEventData,
    },
    handleRescheduleClick,
  }
}
